// RFC-1371 REST client for team accounts and membership.
//
// Mirrors the server contract in
// `packages/core-schemas/.../api_contracts.py` ("Teams (RFC-1371)") and
// `apps/api/.../routes/teams.py`. Field names and envelopes here are the real
// ones, not assumed: `my_role` not `role`, bare `TeamSummary` bodies rather
// than `{team: …}` wrappers, `204` on a role change, `expires_in_days` on an
// invite.
//
// Requests go through `apiFetch` (api/client.ts) so they inherit the resolved
// API base, the GoTrue bearer, `credentials: 'include'` (LB session affinity —
// it breaks silently without this) and the transient-retry policy. We use
// `apiFetch` over `apiRequest` because almost every call site narrows on the
// server's STABLE ERROR CODE rather than on a status alone.
//
// ── Errors are a contract, not a string ────────────────────────────────────
//
// Every failure body is `{"detail": {"error": "<stable_code>", …}}`. Branch on
// `TeamsRequestError.code`, never on the message: the codes are the API, the
// prose is not. Two of them are not failures at all in the UI sense — they
// carry the payload a dialog needs:
//
//   `private_projects_disposition_required` (409) IS the leave prompt. It
//   names the projects at risk, and re-POSTing with the answer completes the
//   leave. Modelling it as a preview call would let the list go stale between
//   the read and the write.
//
//   `team_not_empty` (409) splits `live_projects` / `trashed_projects`,
//   because `projects.owner_team_id` is a real FK with no ON DELETE and a
//   SOFT-deleted project still blocks deletion. A team that looks empty and
//   refuses to delete is otherwise inexplicable.
//
// ── Project visibility rides on the project payload ────────────────────────
//
// There is no `/projects/{id}/visibility` route and there should not be:
// `visibility`, `owner_team_id` and `can_publish` come back on the existing
// `GET /projects/{id}` response (see `Project` in api/types.ts), so the
// workspace pill costs no extra fetch. `can_publish` is computed server-side
// from the same predicate the write enforces — trust it rather than
// re-deriving §6 in the client.
//
// ── One endpoint does not exist yet ────────────────────────────────────────
//
// `getTeamCredits` is written against the shape it will have, but RFC-1372's
// wallet is schema-only — nothing reads or writes it — so it is gated by
// `TEAM_CREDITS_ENDPOINT_AVAILABLE` and issues no traffic.

import { apiFetch, type ApiFetchOptions } from './client'
import { isApiRecord, type ApiRecord, type ApiValue } from './responseDecoders'

// ---------------------------------------------------------------------------
// Pending-endpoint gates
// ---------------------------------------------------------------------------

/**
 * `GET /teams/{id}/credits` — live.
 *
 * Shipped with the team wallet (093's `team_spark_balances`). Kept as a named
 * constant rather than deleted: it is the one switch that turns the C9 history
 * and the dropdown balance back into an explicit "not available" state if the
 * endpoint ever has to be withdrawn, without unpicking the components.
 */
export const TEAM_CREDITS_ENDPOINT_AVAILABLE: boolean = true

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type TeamRole = 'owner' | 'admin' | 'member'
export type TeamKind = 'personal' | 'team'
export type ProjectVisibility = 'private' | 'team'
/** An invite can never confer Owner — that arrives by succession or transfer. */
export type InvitableRole = 'admin' | 'member'
/** §7.3's question, asked only of a voluntary leaver. */
export type PrivateProjectDisposition = 'publish' | 'discard'

export const TEAM_NAME_MAX_LENGTH = 100
export const INVITE_MAX_TTL_DAYS = 30

export interface Team {
  id: string
  name: string
  avatarUrl: string | null
  kind: TeamKind
  memberLimit: number
  memberCount: number
  /** The signed-in user's role in THIS team. Drives every §6 gate. */
  myRole: TeamRole
  createdAt: string
  creditBillingMode?: 'prepaid' | 'postpaid'
  billingPeriodAnchor?: string | null
  billingPeriodMonths?: 1 | 3 | 6 | 12 | null
  billingTimezone?: string | null
}

export interface TeamUsageProjectGroup {
  projectId: string | null
  label: string
  credits: number
}

export interface TeamUsageMemberGroup {
  userId: string | null
  label: string
  email: string | null
  credits: number
}

export interface TeamUsage {
  teamId: string
  usagePeriodId?: string | null
  contractId?: string | null
  isClosed?: boolean
  periodStart: string
  periodEnd: string
  periodStartDate: string
  periodEndDate: string
  timezone: string
  projectId: string | null
  totalCredits: number
  projects: TeamUsageProjectGroup[]
  members: TeamUsageMemberGroup[]
}

export interface TeamUsagePeriod {
  usagePeriodId: string | null
  contractId: string
  periodStart: string
  periodEnd: string
  periodStartDate: string
  periodEndDate: string
  timezone: string
  isClosed: boolean
  billableCredits: number | null
  closedAt: string | null
}

/**
 * A member row.
 *
 * The identity fields are nullable and that is load-bearing, not defensive.
 * These ids come from GoTrue, API keys, service accounts, the debug user and
 * `PUBLIC_USER_ID` — and only the first has an `auth.users` row to join
 * against. So a null name is a PERMANENT property of that principal, not a
 * failed lookup, which is exactly why this is a widened row rather than a
 * separate endpoint that would have had to 404 at something unremarkable.
 *
 * Render the name when present and a truncated id when not. Never treat null
 * as an error.
 */
export interface TeamMember {
  userId: string
  role: TeamRole
  invitedBy: string | null
  joinedAt: string
  displayName: string | null
  email: string | null
  avatarUrl: string | null
}

export interface TeamInvite {
  id: string
  teamId: string
  roleOnJoin: InvitableRole
  expiresAt: string
  /** null = unlimited until expiry. */
  maxUses: number | null
  useCount: number
  createdBy: string
  createdAt: string
  revokedAt: string | null
}

/**
 * The create-invite result — the ONLY response that ever carries the token.
 *
 * Only the SHA-256 hash is persisted, so this cannot be re-read from anywhere.
 * A lost link is reissued, not recovered, and the UI has to say so.
 */
export interface CreateInviteResult {
  invite: TeamInvite
  token: string
}

export interface TeamProjectRef {
  projectId: string
  name: string
}

export interface LeaveTeamResult {
  teamId: string
  /** Set when the Owner left and D7 appointed a successor. */
  successorUserId: string | null
  publishedProjectIds: string[]
  discardedProjectIds: string[]
}

export interface RemoveMemberResult {
  teamId: string
  removedUserId: string
  successorUserId: string | null
  /** §7.3: an admin removal AUTO-publishes. Irreversible. */
  publishedProjectIds: string[]
}

export interface RedeemInviteResult {
  teamId: string
  role: TeamRole
  /** Redemption is idempotent; a repeat does not burn one of the link's uses. */
  alreadyMember: boolean
}

export interface PublishProjectResult {
  projectId: string
  visibility: 'team'
  publishedToTeamBy: string | null
  publishedToTeamAt: string | null
  /** False when it was already team-visible; original attribution preserved. */
  changed: boolean
}

export type TeamCreditEntryKind =
  | 'purchase'
  | 'purchase_refund'
  | 'spend'
  | 'refund'
  | 'grant'
  | 'unknown'

/** Shape requested for RFC-1372 C9; see TEAM_CREDITS_ENDPOINT_AVAILABLE. */
export interface TeamCreditEntry {
  id: string
  kind: TeamCreditEntryKind
  /** Signed credit delta: positive in, negative out. */
  credits: number
  occurredAt: string
  /** Null for a de-identified row — a departed account (RFC-1372 C10). */
  actorUserId: string | null
  actorDisplayName: string | null
  projectId: string | null
  projectName: string | null
  /** Purchase rows only: what the buyer actually paid. */
  amountMinor: number | null
  currency: string | null
}

export type AllowanceLimitType = 'no_limit' | 'fixed' | 'recurring'
export type AllowancePeriod = 'daily' | 'weekly' | 'monthly'

/**
 * One member's spending cap against the team's pooled credits.
 *
 * A cap, not a sub-wallet: nothing is reserved, so caps may sum to more than
 * the balance and the pool stays the hard ceiling.
 *
 * `remaining` is null for uncapped, and already accounts for a recurring
 * period that has rolled over — it is what the charge path itself would use.
 */
export interface MemberAllowance {
  userId: string
  limitType: AllowanceLimitType
  limitAmount: number | null
  period: AllowancePeriod | null
  /** Against the CURRENT period for recurring, lifetime for fixed. */
  consumed: number
  remaining: number | null
}

export interface TeamCreditHistory {
  /** Null when the team has no wallet row yet — never funded, NOT zero. The
   *  row is created lazily on first credit movement, so rendering null as "0
   *  credits" would claim the team had spent everything. */
  balance: number | null
  /** One page of the ledger, newest first. */
  entries: TeamCreditEntry[]
  /** How many entries the team's whole ledger holds, for the page count. */
  total: number
}

export type TeamDeletionBlockReason = 'credit_history' | 'generation_jobs' | 'checkout_orders' | 'team_styles' | 'provider_configs' | 'projects'

export interface TeamDeletionPreview {
  canDelete: boolean
  reasons: TeamDeletionBlockReason[]
  liveProjects: number
  trashedProjects: number
}

// ---------------------------------------------------------------------------
// Error carrier
// ---------------------------------------------------------------------------

/**
 * A failed teams request, carrying the server's stable error code.
 *
 * `code` is the thing to branch on. The status alone is not enough: 409 covers
 * the leave prompt, a non-empty team, the member limit and the last-member
 * block, and every one of those needs different copy and a different control.
 */
export class TeamsRequestError extends Error {
  readonly status: number
  readonly code: string | null
  /** The whole `detail` object, for codes that carry extra fields. */
  readonly detail: ApiRecord | null

  constructor(status: number, body: ApiValue) {
    const detail = isApiRecord(body) && isApiRecord(body.detail) ? body.detail : null
    const rawCode = detail === null ? undefined : detail.error
    const code = typeof rawCode === 'string' ? rawCode : null
    super(`teams request failed: ${status}${code === null ? '' : ` (${code})`}`)
    this.name = 'TeamsRequestError'
    this.status = status
    this.code = code
    this.detail = detail
  }

  /** A server-supplied sentence, when the code carries one. */
  get message_(): string | null {
    if (this.detail === null) return null
    const message = this.detail.message
    return typeof message === 'string' && message !== '' ? message : null
  }
}

/**
 * The §7.3 leave prompt, when that is what came back.
 *
 * Returns the projects the departing member must decide about, or null when
 * this error is something else. Callers re-POST with a disposition.
 */
export function leaveNeedsDisposition(error: Error): TeamProjectRef[] | null {
  if (!(error instanceof TeamsRequestError)) return null
  if (error.code !== 'private_projects_disposition_required') return null
  const raw = error.detail === null ? undefined : error.detail.projects
  if (!Array.isArray(raw)) return []
  const refs: TeamProjectRef[] = []
  for (const item of raw) {
    if (!isApiRecord(item)) continue
    const projectId = item.project_id
    const name = item.name
    if (typeof projectId !== 'string') continue
    refs.push({ projectId, name: typeof name === 'string' ? name : projectId })
  }
  return refs
}

/**
 * The blocked-delete counts, when that is what came back.
 *
 * `trashed` is the one that surprises people: a soft-deleted project still
 * holds the FK, so a team that looks empty refuses to delete.
 */
export function teamNotEmptyCounts(
  error: Error,
): { live: number; trashed: number } | null {
  if (!(error instanceof TeamsRequestError)) return null
  if (error.code !== 'team_not_empty') return null
  const detail = error.detail
  if (detail === null) return { live: 0, trashed: 0 }
  const live = detail.live_projects
  const trashed = detail.trashed_projects
  return {
    live: typeof live === 'number' ? live : 0,
    trashed: typeof trashed === 'number' ? trashed : 0,
  }
}

// ---------------------------------------------------------------------------
// Boundary decoding
//
// Hand-written guards, because the console bans `as`/`any`/`unknown`
// (npm run lint:types). A drifted field fails here with its own name rather
// than surfacing as `undefined` three components later.
// ---------------------------------------------------------------------------

function str(record: ApiRecord, key: string, context: string): string {
  const value = record[key]
  if (typeof value !== 'string') throw new Error(`${context}.${key} must be a string`)
  return value
}

function strOrNull(record: ApiRecord, key: string, context: string): string | null {
  const value = record[key]
  if (value === null || value === undefined) return null
  if (typeof value !== 'string') throw new Error(`${context}.${key} must be a string or null`)
  return value
}

function num(record: ApiRecord, key: string, context: string): number {
  const value = record[key]
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    throw new Error(`${context}.${key} must be a number`)
  }
  return value
}

function numOrNull(record: ApiRecord, key: string, context: string): number | null {
  const value = record[key]
  if (value === null || value === undefined) return null
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    throw new Error(`${context}.${key} must be a number or null`)
  }
  return value
}

function bool(record: ApiRecord, key: string, context: string): boolean {
  const value = record[key]
  if (typeof value !== 'boolean') throw new Error(`${context}.${key} must be a boolean`)
  return value
}

function obj(raw: ApiValue, context: string): ApiRecord {
  if (!isApiRecord(raw)) throw new Error(`${context} must be an object`)
  return raw
}

function role(record: ApiRecord, key: string, context: string): TeamRole {
  const value = record[key]
  if (value !== 'owner' && value !== 'admin' && value !== 'member') {
    throw new Error(`${context}.${key} must be a team role`)
  }
  return value
}

function kind(record: ApiRecord, key: string, context: string): TeamKind {
  const value = record[key]
  if (value !== 'personal' && value !== 'team') {
    throw new Error(`${context}.${key} must be a team kind`)
  }
  return value
}

function stringArray(record: ApiRecord, key: string, context: string): string[] {
  const value = record[key]
  if (value === undefined || value === null) return []
  if (!Array.isArray(value)) throw new Error(`${context}.${key} must be an array`)
  return value.filter((item) => typeof item === 'string')
}

function decodeTeam(raw: ApiValue, context: string): Team {
  const team = obj(raw, context)
  const rawBillingMode = team.credit_billing_mode
  const billingMode = rawBillingMode === undefined ? 'prepaid' : rawBillingMode
  if (billingMode !== 'prepaid' && billingMode !== 'postpaid') {
    throw new Error(`${context}.credit_billing_mode must be prepaid or postpaid`)
  }
  const periodMonths = team.billing_period_months === undefined
    ? null
    : numOrNull(team, 'billing_period_months', context)
  if (periodMonths !== null && periodMonths !== 1 && periodMonths !== 3 && periodMonths !== 6 && periodMonths !== 12) {
    throw new Error(`${context}.billing_period_months must be 1, 3, 6, or 12`)
  }
  return {
    id: str(team, 'id', context),
    name: str(team, 'name', context),
    avatarUrl: strOrNull(team, 'avatar_url', context),
    kind: kind(team, 'kind', context),
    memberLimit: num(team, 'member_limit', context),
    memberCount: num(team, 'member_count', context),
    myRole: role(team, 'my_role', context),
    createdAt: str(team, 'created_at', context),
    creditBillingMode: billingMode,
    billingPeriodAnchor: team.billing_period_anchor === undefined
      ? null
      : strOrNull(team, 'billing_period_anchor', context),
    billingPeriodMonths: periodMonths,
    billingTimezone: team.billing_timezone === undefined
      ? null
      : strOrNull(team, 'billing_timezone', context),
  }
}

function decodeMember(raw: ApiValue, context: string): TeamMember {
  const member = obj(raw, context)
  return {
    userId: str(member, 'user_id', context),
    role: role(member, 'role', context),
    invitedBy: strOrNull(member, 'invited_by', context),
    joinedAt: str(member, 'joined_at', context),
    displayName: strOrNull(member, 'display_name', context),
    email: strOrNull(member, 'email', context),
    avatarUrl: strOrNull(member, 'avatar_url', context),
  }
}

function decodeInvite(raw: ApiValue, context: string): TeamInvite {
  const invite = obj(raw, context)
  const roleOnJoin = invite.role_on_join
  if (roleOnJoin !== 'admin' && roleOnJoin !== 'member') {
    throw new Error(`${context}.role_on_join must be admin or member`)
  }
  return {
    id: str(invite, 'id', context),
    teamId: str(invite, 'team_id', context),
    roleOnJoin,
    expiresAt: str(invite, 'expires_at', context),
    maxUses: numOrNull(invite, 'max_uses', context),
    useCount: num(invite, 'use_count', context),
    createdBy: str(invite, 'created_by', context),
    createdAt: str(invite, 'created_at', context),
    revokedAt: strOrNull(invite, 'revoked_at', context),
  }
}

function decodeArray<T>(
  envelope: ApiRecord,
  key: string,
  context: string,
  decodeItem: (item: ApiValue, itemContext: string) => T,
): T[] {
  const items = envelope[key]
  if (!Array.isArray(items)) throw new Error(`${context}.${key} must be an array`)
  return items.map((item, index) => decodeItem(item, `${context}.${key}[${index}]`))
}

export function decodeTeamList(raw: ApiValue): Team[] {
  const context = 'teams response'
  return decodeArray(obj(raw, context), 'teams', context, decodeTeam)
}

export function decodeMemberList(raw: ApiValue): TeamMember[] {
  const context = 'team members response'
  return decodeArray(obj(raw, context), 'members', context, decodeMember)
}

export function decodeInviteList(raw: ApiValue): TeamInvite[] {
  const context = 'team invites response'
  return decodeArray(obj(raw, context), 'invites', context, decodeInvite)
}

function decodeCreateInvite(raw: ApiValue): CreateInviteResult {
  const context = 'create invite response'
  const envelope = obj(raw, context)
  return {
    invite: decodeInvite(envelope.invite, `${context}.invite`),
    token: str(envelope, 'token', context),
  }
}

function decodeLeave(raw: ApiValue): LeaveTeamResult {
  const context = 'leave team response'
  const envelope = obj(raw, context)
  return {
    teamId: str(envelope, 'team_id', context),
    successorUserId: strOrNull(envelope, 'successor_user_id', context),
    publishedProjectIds: stringArray(envelope, 'published_project_ids', context),
    discardedProjectIds: stringArray(envelope, 'discarded_project_ids', context),
  }
}

export function decodeTeamUsagePeriods(raw: ApiValue): TeamUsagePeriod[] {
  const context = 'team usage periods response'
  const envelope = obj(raw, context)
  if (!Array.isArray(envelope.periods)) throw new Error(`${context} requires periods`)
  return envelope.periods.map((rawPeriod, index) => {
    const period = obj(rawPeriod, `${context}.periods[${index}]`)
    return {
      usagePeriodId: strOrNull(period, 'usage_period_id', context),
      contractId: str(period, 'contract_id', context),
      periodStart: str(period, 'period_start', context),
      periodEnd: str(period, 'period_end', context),
      periodStartDate: str(period, 'period_start_date', context),
      periodEndDate: str(period, 'period_end_date', context),
      timezone: str(period, 'timezone', context),
      isClosed: bool(period, 'is_closed', context),
      billableCredits: numOrNull(period, 'billable_credits', context),
      closedAt: strOrNull(period, 'closed_at', context),
    }
  })
}

function decodeRemoveMember(raw: ApiValue): RemoveMemberResult {
  const context = 'remove member response'
  const envelope = obj(raw, context)
  return {
    teamId: str(envelope, 'team_id', context),
    removedUserId: str(envelope, 'removed_user_id', context),
    successorUserId: strOrNull(envelope, 'successor_user_id', context),
    publishedProjectIds: stringArray(envelope, 'published_project_ids', context),
  }
}

function decodeRedeem(raw: ApiValue): RedeemInviteResult {
  const context = 'redeem invite response'
  const envelope = obj(raw, context)
  return {
    teamId: str(envelope, 'team_id', context),
    role: role(envelope, 'role', context),
    alreadyMember: bool(envelope, 'already_member', context),
  }
}

function decodePublish(raw: ApiValue): PublishProjectResult {
  const context = 'publish project response'
  const envelope = obj(raw, context)
  const visibility = envelope.visibility
  if (visibility !== 'team') {
    throw new Error(`${context}.visibility must be team`)
  }
  return {
    projectId: str(envelope, 'project_id', context),
    visibility,
    publishedToTeamBy: strOrNull(envelope, 'published_to_team_by', context),
    publishedToTeamAt: strOrNull(envelope, 'published_to_team_at', context),
    changed: bool(envelope, 'changed', context),
  }
}

/**
 * `spark_transaction_type` (the ledger enum) -> the product vocabulary this
 * module exposes.
 *
 * The API returns the enum verbatim (`teams.py`'s `t.type::text AS kind`,
 * declared as a `Literal` in `api_contracts.py`), and the two vocabularies
 * overlap only on `refund`. Without this map every real entry threw, and
 * because the throw happens inside the query function React Query failed the
 * WHOLE query — so a single `welcome_bonus` row blanked the balance card with
 * "Could not load the team balance" even though `balance` had decoded fine.
 *
 * Known server kinds get specific product labels. New string kinds use the
 * generic `unknown` label while retaining the row and balance; malformed
 * fields still fail typed decoding.
 */
const CREDIT_ENTRY_KINDS: Readonly<Record<string, TeamCreditEntryKind>> = {
  topup: 'purchase',
  charge: 'spend',
  refund: 'refund',
  purchase_refund: 'purchase_refund',
  welcome_bonus: 'grant',
  daily_claim: 'grant',
}

function decodeCreditEntry(raw: ApiValue, context: string): TeamCreditEntry {
  const entry = obj(raw, context)
  const rawKind = str(entry, 'kind', context)
  const entryKind = Object.prototype.hasOwnProperty.call(CREDIT_ENTRY_KINDS, rawKind)
    ? CREDIT_ENTRY_KINDS[rawKind]
    : 'unknown'
  return {
    id: str(entry, 'id', context),
    kind: entryKind,
    credits: num(entry, 'credits', context),
    occurredAt: str(entry, 'occurred_at', context),
    actorUserId: strOrNull(entry, 'actor_user_id', context),
    actorDisplayName: strOrNull(entry, 'actor_display_name', context),
    projectId: strOrNull(entry, 'project_id', context),
    projectName: strOrNull(entry, 'project_name', context),
    amountMinor: numOrNull(entry, 'amount_minor', context),
    currency: strOrNull(entry, 'currency', context),
  }
}

function decodeAllowance(raw: ApiValue, context: string): MemberAllowance {
  const entry = obj(raw, context)
  const limitType = entry.limit_type
  if (limitType !== 'no_limit' && limitType !== 'fixed' && limitType !== 'recurring') {
    throw new Error(`${context}.limit_type must be an allowance limit type`)
  }
  const period = entry.period
  if (period !== null && period !== undefined) {
    if (period !== 'daily' && period !== 'weekly' && period !== 'monthly') {
      throw new Error(`${context}.period must be an allowance period`)
    }
  }
  return {
    userId: str(entry, 'user_id', context),
    limitType,
    limitAmount: numOrNull(entry, 'limit_amount', context),
    period: period === null || period === undefined ? null : period,
    consumed: num(entry, 'consumed', context),
    // null is UNLIMITED here, not zero. Collapsing the two would render an
    // uncapped member as one who may spend nothing.
    remaining: numOrNull(entry, 'remaining', context),
  }
}

export function decodeAllowanceList(raw: ApiValue): MemberAllowance[] {
  const context = 'team allowances response'
  const envelope = obj(raw, context)
  return decodeArray(envelope, 'allowances', context, decodeAllowance)
}

export function decodeCreditHistory(raw: ApiValue): TeamCreditHistory {
  const context = 'team credits response'
  const envelope = obj(raw, context)
  return {
    balance: numOrNull(envelope, 'balance', context),
    entries: decodeArray(envelope, 'entries', context, decodeCreditEntry),
    total: num(envelope, 'total', context),
  }
}

export function decodeTeamUsage(raw: ApiValue): TeamUsage {
  const context = 'team usage response'
  const envelope = obj(raw, context)
  const projectsRaw = envelope.projects
  const membersRaw = envelope.members
  if (!Array.isArray(projectsRaw) || !Array.isArray(membersRaw)) {
    throw new Error(`${context} requires projects and members arrays`)
  }
  return {
    teamId: str(envelope, 'team_id', context),
    usagePeriodId: strOrNull(envelope, 'usage_period_id', context),
    contractId: strOrNull(envelope, 'contract_id', context),
    isClosed: typeof envelope.is_closed === 'boolean' ? envelope.is_closed : false,
    periodStart: str(envelope, 'period_start', context),
    periodEnd: str(envelope, 'period_end', context),
    periodStartDate: str(envelope, 'period_start_date', context),
    periodEndDate: str(envelope, 'period_end_date', context),
    timezone: str(envelope, 'timezone', context),
    projectId: strOrNull(envelope, 'project_id', context),
    totalCredits: num(envelope, 'total_credits', context),
    projects: projectsRaw.map((rawProject, index) => {
      const project = obj(rawProject, `${context}.projects[${index}]`)
      return {
        projectId: strOrNull(project, 'project_id', context),
        label: str(project, 'label', context),
        credits: num(project, 'credits', context),
      }
    }),
    members: membersRaw.map((rawMember, index) => {
      const member = obj(rawMember, `${context}.members[${index}]`)
      return {
        userId: strOrNull(member, 'user_id', context),
        label: str(member, 'label', context),
        email: strOrNull(member, 'email', context),
        credits: num(member, 'credits', context),
      }
    }),
  }
}

export function decodeTeamDeletionPreview(raw: ApiValue): TeamDeletionPreview {
  const context = 'team deletion preview'
  const envelope = obj(raw, context)
  if (typeof envelope.can_delete !== 'boolean' || !Array.isArray(envelope.reasons)) {
    throw new Error(`${context} requires deletion eligibility and reasons`)
  }
  const reasons: TeamDeletionBlockReason[] = envelope.reasons.map((reason) => {
    if (reason !== 'credit_history' && reason !== 'generation_jobs' && reason !== 'checkout_orders' && reason !== 'team_styles' && reason !== 'provider_configs' && reason !== 'projects') {
      throw new Error(`${context} has an unknown deletion reason`)
    }
    return reason
  })
  const liveProjects = num(envelope, 'live_projects', context)
  const trashedProjects = num(envelope, 'trashed_projects', context)
  if (![liveProjects, trashedProjects].every((count) => Number.isSafeInteger(count) && count >= 0)) {
    throw new Error(`${context} counts must be safe integers`)
  }
  if (envelope.can_delete !== (reasons.length === 0) ||
      reasons.includes('projects') !== (liveProjects + trashedProjects > 0)) {
    throw new Error(`${context} has inconsistent deletion eligibility`)
  }
  return { canDelete: envelope.can_delete, reasons, liveProjects, trashedProjects }
}

// ---------------------------------------------------------------------------
// Transport
// ---------------------------------------------------------------------------

const JSON_HEADERS = { 'Content-Type': 'application/json' }

// Mutations never retry. `apiFetch` retries 5xx on idempotent methods only,
// but these are explicit because several are genuinely non-idempotent: a
// retried POST /teams mints a second team, and a retried leave can race D7's
// succession.
const MUTATION: ApiFetchOptions = { retryTransient: false, headers: JSON_HEADERS }

const seg = encodeURIComponent

async function readError(response: Response): Promise<TeamsRequestError> {
  const body: ApiValue = await response.json().catch(() => null)
  return new TeamsRequestError(response.status, body)
}

async function request<T>(
  path: string,
  decode: (raw: ApiValue) => T,
  init?: ApiFetchOptions,
): Promise<T> {
  const response = await apiFetch(path, init)
  if (!response.ok) throw await readError(response)
  const raw: ApiValue = await response.json()
  return decode(raw)
}

/** For 204 endpoints. Still throws the typed, code-carrying error. */
async function command(path: string, init: ApiFetchOptions): Promise<void> {
  const response = await apiFetch(path, init)
  if (!response.ok) throw await readError(response)
}

// ── Teams ──────────────────────────────────────────────────────────────────

export async function listTeams(): Promise<Team[]> {
  return request('/teams', decodeTeamList)
}

export async function getTeam(teamId: string): Promise<Team> {
  return request(`/teams/${seg(teamId)}`, (raw) => decodeTeam(raw, 'team'))
}

/**
 * Create a team. Always `kind: "team"` — the console can never mint a personal
 * team; those come from a DB trigger at signup (D1/D10).
 */
export async function createTeam(name: string): Promise<Team> {
  return request('/teams', (raw) => decodeTeam(raw, 'create team response'), {
    ...MUTATION,
    method: 'POST',
    body: JSON.stringify({ name }),
  })
}

/** Owner only (§6 gives an Admin nothing here). Omitted fields are unchanged. */
export async function updateTeam(
  teamId: string,
  changes: { name?: string; avatarUrl?: string | null; memberLimit?: number },
): Promise<Team> {
  const body: Record<string, string | number | null> = {}
  if (changes.name !== undefined) body.name = changes.name
  if (changes.avatarUrl !== undefined) body.avatar_url = changes.avatarUrl
  if (changes.memberLimit !== undefined) body.member_limit = changes.memberLimit
  return request(`/teams/${seg(teamId)}`, (raw) => decodeTeam(raw, 'update team response'), {
    ...MUTATION,
    method: 'PATCH',
    body: JSON.stringify(body),
  })
}

/** Owner only, `kind: "team"` only, and the team must hold zero projects. */
export async function deleteTeam(teamId: string): Promise<void> {
  return command(`/teams/${seg(teamId)}`, { ...MUTATION, method: 'DELETE' })
}

// ── Members ────────────────────────────────────────────────────────────────

export async function listMembers(teamId: string): Promise<TeamMember[]> {
  return request(`/teams/${seg(teamId)}/members`, decodeMemberList)
}

/**
 * Set a member's role. Returns nothing — refetch the list.
 *
 * `role: 'owner'` is an ownership TRANSFER: the current Owner is demoted to
 * Admin in the same transaction, and only the Owner may send it.
 */
export async function changeMemberRole(
  teamId: string,
  userId: string,
  nextRole: TeamRole,
): Promise<void> {
  return command(`/teams/${seg(teamId)}/members/${seg(userId)}`, {
    ...MUTATION,
    method: 'PATCH',
    body: JSON.stringify({ role: nextRole }),
  })
}

/**
 * Remove a member (Owner/Admin).
 *
 * §7.3: this AUTO-PUBLISHES the removed member's private projects to the team,
 * irreversibly. The returned `published_project_ids` is the receipt.
 */
export async function removeMember(
  teamId: string,
  userId: string,
): Promise<RemoveMemberResult> {
  return request(`/teams/${seg(teamId)}/members/${seg(userId)}`, decodeRemoveMember, {
    ...MUTATION,
    method: 'DELETE',
  })
}

/**
 * Leave a team.
 *
 * Call with `null` first. If the leaver has private projects here the server
 * answers 409 `private_projects_disposition_required` naming them — that
 * response IS the prompt (see `leaveNeedsDisposition`). Re-call with the
 * user's answer. There is no default, deliberately: one would turn a single
 * click into either an involuntary disclosure or silent data loss.
 */
export async function leaveTeam(
  teamId: string,
  privateProjects: PrivateProjectDisposition | null,
): Promise<LeaveTeamResult> {
  return request(`/teams/${seg(teamId)}/leave`, decodeLeave, {
    ...MUTATION,
    method: 'POST',
    body: JSON.stringify({ private_projects: privateProjects }),
  })
}

// ── Invites ────────────────────────────────────────────────────────────────

export async function listInvites(teamId: string): Promise<TeamInvite[]> {
  return request(`/teams/${seg(teamId)}/invites`, decodeInviteList)
}

export async function createInvite(
  teamId: string,
  options: {
    roleOnJoin: InvitableRole
    expiresInDays: number
    maxUses: number | null
  },
): Promise<CreateInviteResult> {
  return request(`/teams/${seg(teamId)}/invites`, decodeCreateInvite, {
    ...MUTATION,
    method: 'POST',
    body: JSON.stringify({
      role_on_join: options.roleOnJoin,
      expires_in_days: options.expiresInDays,
      max_uses: options.maxUses,
    }),
  })
}

export async function revokeInvite(teamId: string, inviteId: string): Promise<void> {
  return command(`/teams/${seg(teamId)}/invites/${seg(inviteId)}`, {
    ...MUTATION,
    method: 'DELETE',
  })
}

/** What an unredeemed token will tell you about its team. Name only. */
export interface InvitePreview {
  teamName: string
}

/**
 * Look at an invite without accepting it.
 *
 * Unauthenticated-safe and deliberately thin — a team NAME and nothing else.
 * The token is a bearer credential sitting in someone's chat client, so the
 * preview exposes only what the sender already told the recipient.
 *
 * ASSUMED FIELD NAME: `team_name`, falling back to `name`. The endpoint was
 * agreed as "team name only" without the key being pinned down, so the decoder
 * accepts either rather than betting on one. This is the single place to
 * tighten once the shape is confirmed.
 *
 * Treat a failure here as cosmetic. The join page falls back to generic copy —
 * a preview that cannot load must never block a redemption that would work,
 * because the token is the thing that matters and this call is decoration.
 */
export async function getInvitePreview(token: string): Promise<InvitePreview> {
  return request(`/teams/invites/${seg(token)}`, (raw) => {
    const context = 'invite preview'
    const envelope = obj(raw, context)
    const named = envelope.team_name ?? envelope.name
    if (typeof named !== 'string' || named === '') {
      throw new Error(`${context} must carry a team name`)
    }
    return { teamName: named }
  })
}

/**
 * Redeem an invite token.
 *
 * Note the path: it is NOT under a team id, because the redeemer does not know
 * the team until this call answers.
 */
export async function redeemInvite(token: string): Promise<RedeemInviteResult> {
  return request('/teams/invites/redeem', decodeRedeem, {
    ...MUTATION,
    method: 'POST',
    body: JSON.stringify({ token }),
  })
}

/** The console route an invite token resolves to. */
export const INVITE_JOIN_PATH = '/join'

/**
 * Turn a raw token into a shareable link.
 *
 * The API returns a token, not a URL, because it does not know the console
 * origin. Building it here keeps the one place that knows the join route next
 * to the one place that knows the token format.
 */
export function buildInviteUrl(token: string): string {
  return `${window.location.origin}${INVITE_JOIN_PATH}/${seg(token)}`
}

// ── Projects ───────────────────────────────────────────────────────────────

/**
 * Publish a project to its owning team (D3).
 *
 * Creator-only: §6 makes a private project invisible to Owners and Admins too,
 * so nobody else can publish it. One-way — there is no un-publish route and
 * there never will be, which is why this module offers no shape for one.
 */
export async function publishProjectToTeam(
  projectId: string,
): Promise<PublishProjectResult> {
  return request(`/projects/${seg(projectId)}/publish-to-team`, decodePublish, {
    ...MUTATION,
    method: 'POST',
  })
}

/** Ledger entries per page of the team credit history. */
export const TEAM_CREDITS_PAGE_SIZE = 10

/** The balance plus one page of the ledger. `page` is 0-based, newest first. */
export async function getTeamCredits(teamId: string, page: number): Promise<TeamCreditHistory> {
  const params = new URLSearchParams({
    limit: String(TEAM_CREDITS_PAGE_SIZE),
    offset: String(page * TEAM_CREDITS_PAGE_SIZE),
  })
  return request(`/teams/${seg(teamId)}/credits?${params.toString()}`, decodeCreditHistory)
}

export async function getTeamUsage(
  teamId: string,
  projectId?: string,
  usagePeriodId?: string,
): Promise<TeamUsage> {
  const params = new URLSearchParams()
  if (projectId) params.set('project_id', projectId)
  if (usagePeriodId) params.set('usage_period_id', usagePeriodId)
  const query = params.size ? `?${params.toString()}` : ''
  return request(`/teams/${seg(teamId)}/usage${query}`, decodeTeamUsage)
}

export async function getTeamUsagePeriods(teamId: string): Promise<TeamUsagePeriod[]> {
  return request(`/teams/${seg(teamId)}/usage/periods`, decodeTeamUsagePeriods)
}

/** Snapshot for the owner confirmation dialog; DELETE still checks its rules. */
export async function getTeamDeletionPreview(teamId: string): Promise<TeamDeletionPreview> {
  return request(`/teams/${seg(teamId)}/deletion-preview`, decodeTeamDeletionPreview)
}


// ── Member spending caps (RFC-1372 C3) ─────────────────────────────────────

/** Every cap set in the team. Owner/Admin only; members are 403. */
export async function listAllowances(teamId: string): Promise<MemberAllowance[]> {
  return request(`/teams/${seg(teamId)}/allowances`, decodeAllowanceList)
}

/**
 * Set one member's cap (Owner/Admin).
 *
 * PUT with the whole shape, because the three limit types are mutually
 * exclusive — a partial update could not say what `period` means when the type
 * is changing to `fixed` in the same call.
 */
export async function setMemberAllowance(
  teamId: string,
  userId: string,
  body: { limitType: AllowanceLimitType; limitAmount: number | null; period: AllowancePeriod | null },
): Promise<MemberAllowance> {
  return request(
    `/teams/${seg(teamId)}/members/${seg(userId)}/allowance`,
    (raw) => decodeAllowance(raw, 'member allowance'),
    {
      ...MUTATION,
      method: 'PUT',
      body: JSON.stringify({
        limit_type: body.limitType,
        limit_amount: body.limitAmount,
        period: body.period,
      }),
    },
  )
}

/** Remove the cap, returning the member to uncapped (Owner/Admin). */
export async function clearMemberAllowance(teamId: string, userId: string): Promise<void> {
  return command(`/teams/${seg(teamId)}/members/${seg(userId)}/allowance`, {
    ...MUTATION,
    method: 'DELETE',
  })
}
